// Which workspace does a notification belong to?
//
// The inbox is NOT single-workspace. `workspaceProject.notifications list` is
// scoped by the ambient claim, but the ROWS carry hrefs minted elsewhere:
// server-side ticket notifications build `/w/<orgSlug>/<workspaceSlug>/task/<id>`
// (server ticketNotifications.js `buildTicketHref`), so a row can name another
// org entirely — "QA tickets from the system org arriving in your inbox", the
// case openNotificationTarget.js already carries a whole `_hardLoadCrossOrgHref`
// branch for, because clicking one has to hard-load out of the current org.
//
// The "Workspace" tab (2026-08-08 per Nika) is the filter for that: show only
// what belongs to the workspace you are looking at.
//
// PURE path/state predicate — no DOM, no el.call, direct-ESM import like its
// neighbours workspacePrefix.js / openInSlot.js, so the bell panel (shared) and
// the /inbox page (workspace shell) can share one rule.
//
// It deliberately composes the two EXISTING authorities rather than re-deriving
// either:
//   • org       → isCrossOrgNotificationHref (openNotificationTarget.js), whose
//                 active-org read is documented as matching mwtRouter/
//                 workspaceUrlSync so it "can never disagree with the URL
//                 machinery about which org the session is in"
//   • workspace → workspaceUrlHandle (activeWorkspacePointer.js), the ONE rule
//                 every URL stamper uses for the active workspace's segment
// If the tab used its own notion, a row could sit in "Workspace" and still
// hard-navigate you out of the workspace when clicked.
import { getWorkspaceRawSegments, getWorkspacePathHandle } from './workspacePrefix.js'
import { isCrossOrgNotificationHref } from './openNotificationTarget.js'
import { workspaceUrlHandle } from './activeWorkspacePointer.js'

// Every string that addresses the ACTIVE workspace.
//
// The URL comes FIRST and is the reason this is a set rather than one value.
// A workspace is addressable by slug, key or id, and the producer of an href
// and the state record do not have to agree on which — observed live on
// `/w/natali/ws_EEYlHGTvZ8/inbox`, where the notification href carries the
// workspace KEY while `root.workspace` has `slug` and `key` present but
// UNDEFINED and only `id` populated. Comparing against the state record alone
// therefore hid the session's own rows. `currentPath` is where you actually
// are, so it always names this workspace correctly however it is addressed,
// and it self-heals whatever the record is missing.
//
/**
 * True when a notification belongs to the workspace the session is currently
 * in — i.e. clicking it keeps you here.
 *
 * A row with NO `/w/` prefix is workspace-RELATIVE (`/task/<id>`, `/chat`), so
 * it resolves against wherever you are and is by definition current. Only an
 * explicitly-addressed foreign org or foreign workspace is excluded; an
 * unparseable or absent href is never hidden, because a row you cannot place
 * is better shown than silently swallowed.
 *
 * @param {object} notif - a `root.notifs` row ({ href, ... })
 * @param {object} root  - the state root (reads `org` + `workspace`)
 * @returns {boolean}
 *
 * FA204 / frank trap — this whole file is plain arrows with no `this`,
 * consumed only via direct ESM import (see the file-header comment) — same
 * shape as navbarTabMenu.js's navbarTabPrefs. A bare sibling-export read is
 * exactly as unresolvable post-frank.toJSON as a module-local const, so
 * `filterNotifications`/`currentWorkspaceCount` below each carry their own
 * self-contained inline copy of this predicate (and its `_activeHandles`
 * helper) instead of calling this export bare.
 */
export const isCurrentWorkspaceNotification = (notif, root) => {
  // Every string that addresses the ACTIVE workspace — see the file-header
  // comment above for why this is a set rather than one value.
  const activeHandles = (r) => {
    const ws = (r && r.workspace) || null
    const fromUrl = getWorkspaceRawSegments((r && r.currentPath) || '').seg2
    const fromRecord = ws ? [workspaceUrlHandle(ws), ws.slug, ws.key, ws.id, ws._id] : []
    return [fromUrl, ...fromRecord].filter(Boolean).map((v) => String(v).toLowerCase())
  }

  const href = notif && notif.href
  if (!href || typeof href !== 'string') return true
  // Different org → definitively not this workspace.
  if (isCrossOrgNotificationHref(href, root)) return false
  const { orgSlug, seg2 } = getWorkspaceRawSegments(href)
  // No `/w/` prefix at all → workspace-relative → current.
  if (!orgSlug || !seg2) return true
  const handles = activeHandles(root)
  // Cold boot: the session has not resolved a workspace yet, so nothing can be
  // called foreign. Show the whole (already org-checked) list rather than an
  // empty tab that fills in a moment later.
  if (!handles.length) return true
  // Mine by name, whichever field the producer stamped.
  if (handles.indexOf(String(seg2).toLowerCase()) >= 0) return true
  // Segment 2 is only a WORKSPACE when the org actually has one by that handle
  // — `/w/acme/tickets` and `/w/acme/chat` put a ROUTE there (the ambiguity
  // parseWorkspacePath resolves data-driven via the boot-registered resolver).
  // Comparing raw segments instead would hide every org-level route row.
  // Unresolved (resolver not registered yet, or it says "route") falls through
  // to SHOWN: never hide what we cannot positively place as someone else's.
  return getWorkspacePathHandle(href) == null
}

/**
 * The rows a given filter shows. ONE implementation for the bell panel and the
 * /inbox page — the two surfaces are meant to stay byte-identical (see
 * NotificationRow's header) and previously each carried their own `_filtered`.
 *
 * @param {object} root   - state root (`notifs`, `org`, `workspace`)
 * @param {string} filter - 'all' | 'unread' | 'workspace'
 */
export const filterNotifications = (root, filter) => {
  // FA204 / frank trap — self-contained inline copy, see the file-header
  // note on `isCurrentWorkspaceNotification` above.
  const isMine = (notif, r) => {
    const activeHandles = (x) => {
      const ws = (x && x.workspace) || null
      const fromUrl = getWorkspaceRawSegments((x && x.currentPath) || '').seg2
      const fromRecord = ws ? [workspaceUrlHandle(ws), ws.slug, ws.key, ws.id, ws._id] : []
      return [fromUrl, ...fromRecord].filter(Boolean).map((v) => String(v).toLowerCase())
    }
    const href = notif && notif.href
    if (!href || typeof href !== 'string') return true
    if (isCrossOrgNotificationHref(href, r)) return false
    const { orgSlug, seg2 } = getWorkspaceRawSegments(href)
    if (!orgSlug || !seg2) return true
    const handles = activeHandles(r)
    if (!handles.length) return true
    if (handles.indexOf(String(seg2).toLowerCase()) >= 0) return true
    return getWorkspacePathHandle(href) == null
  }

  const all = (root && root.notifs) || []
  if (filter === 'unread') return all.filter((n) => !n.read)
  if (filter === 'workspace') return all.filter((n) => isMine(n, root))
  return all
}

// Count for the tab's own chip — foreign rows are what the tab EXISTS to hide,
// so surfacing "how many are yours" is the useful number.
//
// FA204 / frank trap — self-contained inline copy, see the file-header note
// on `isCurrentWorkspaceNotification` above.
export const currentWorkspaceCount = (root) => {
  const isMine = (notif, r) => {
    const activeHandles = (x) => {
      const ws = (x && x.workspace) || null
      const fromUrl = getWorkspaceRawSegments((x && x.currentPath) || '').seg2
      const fromRecord = ws ? [workspaceUrlHandle(ws), ws.slug, ws.key, ws.id, ws._id] : []
      return [fromUrl, ...fromRecord].filter(Boolean).map((v) => String(v).toLowerCase())
    }
    const href = notif && notif.href
    if (!href || typeof href !== 'string') return true
    if (isCrossOrgNotificationHref(href, r)) return false
    const { orgSlug, seg2 } = getWorkspaceRawSegments(href)
    if (!orgSlug || !seg2) return true
    const handles = activeHandles(r)
    if (!handles.length) return true
    if (handles.indexOf(String(seg2).toLowerCase()) >= 0) return true
    return getWorkspacePathHandle(href) == null
  }
  return ((root && root.notifs) || []).filter((n) => isMine(n, root)).length
}

// Which segment is lit. `All` is the DEFAULT, so it owns unset and any
// unrecognised value — testing `!== 'unread'` (what both surfaces did while
// there were only two tabs) lights All *and* Workspace at the same time the
// moment a third exists. One rule, both surfaces, no drift.
export const isNotifFilterActive = (root, tab) => {
  const f = (root && root._notifFilter) || 'all'
  return tab === 'all' ? f !== 'unread' && f !== 'workspace' : f === tab
}
